import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Control, FieldError, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import type { TCountryCode } from "countries-list";
import CountryFlag from "./countryFlag";
import {
  composePhoneValue,
  countryForDialCode,
  getDialCodeOptions,
  parsePhoneValue,
  type DialCodeOption,
} from "../../utils/phoneNumber";

const LISTBOX_ROLE = "listbox";
const OPTION_ROLE = "option";

interface PhoneInputProps<TFieldValues extends FieldValues> {
  readonly id: string;
  readonly label: string;
  readonly required?: boolean;
  readonly defaultDialCode: string;
  readonly defaultCountry?: string;
  readonly locale?: string;
  readonly error?: FieldError;
  readonly ariaDescribedBy?: string;
  readonly className?: string;
  readonly labelClassName?: string;
  readonly placeholder: string;
  readonly control: Control<TFieldValues>;
  readonly name: FieldPath<TFieldValues>;
  readonly rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
  readonly onCanonicalise: (value: string) => void;
  readonly onChange?: () => void;
}

function valueAsString(value: string | number | readonly string[] | undefined): string {
  return typeof value === "string" ? value : "";
}

function lowerCase(value: string, locale?: string): string {
  try {
    return value.toLocaleLowerCase(locale);
  } catch {
    return value.toLowerCase();
  }
}

function PhoneInput<TFieldValues extends FieldValues>({
  id,
  label,
  required = false,
  defaultDialCode,
  defaultCountry,
  locale,
  error,
  ariaDescribedBy,
  className = "gcw:w-full",
  labelClassName = "",
  placeholder,
  control,
  name,
  rules,
  onCanonicalise,
  onChange,
}: PhoneInputProps<TFieldValues>): React.ReactElement {
  const { t } = useTranslation();
  const { field } = useController({ control, name, rules });
  const options = useMemo(() => getDialCodeOptions(locale), [locale]);
  const preferredCountry = options.find((option) => option.code === defaultCountry?.toUpperCase())?.code;
  const resolvedDefaultCountry = countryForDialCode(defaultDialCode, options, preferredCountry) ?? "GB";
  const initialValue = valueAsString(field.value);
  const initialParts = parsePhoneValue(initialValue, options, resolvedDefaultCountry);
  const [country, setCountry] = useState<TCountryCode>(initialParts.country);
  const [nationalNumber, setNationalNumber] = useState(initialParts.nationalNumber);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const activeOptionRef = useRef<HTMLLIElement>(null);
  const selectedOption = options.find((option) => option.code === country) ?? options[0];
  const selectedIndex = options.findIndex((option) => option.code === country);
  const currentValue = composePhoneValue(selectedOption.code, selectedOption.dialCode, nationalNumber);

  useEffect(() => {
    const value = valueAsString(field.value);
    if (value !== currentValue) {
      const parsed = parsePhoneValue(value, options, resolvedDefaultCountry);
      const parsedOption = options.find((option) => option.code === parsed.country) ?? selectedOption;
      const canonicalValue = composePhoneValue(parsedOption.code, parsedOption.dialCode, parsed.nationalNumber);
      setCountry(parsed.country);
      setNationalNumber(parsed.nationalNumber);
      if (value !== canonicalValue) {
        onCanonicalise(canonicalValue);
      }
    }
  }, [field.value, currentValue, onCanonicalise, options, resolvedDefaultCountry, selectedOption]);

  useEffect(() => {
    if (!open) return;
    filterRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Focus-leave detection for the composite combobox widget; no native equivalent exists.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const handleFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !root.contains(next)) setOpen(false);
    };
    root.addEventListener("focusout", handleFocusOut);
    return () => root.removeEventListener("focusout", handleFocusOut);
  }, []);

  const filteredOptions = useMemo(() => {
    const normalisedFilter = lowerCase(filter.trim(), locale);
    const digitsFilter = filter.replace(/\D/g, "");
    if (!normalisedFilter && !digitsFilter) return options;
    return options.filter((option) => {
      const nameMatches = lowerCase(option.name, locale).includes(normalisedFilter);
      const dialCodeMatches =
        (digitsFilter.length > 0 && option.dialCode.includes(digitsFilter)) ||
        (filter.trim().length > 0 && option.dialCode.includes(filter.trim()));
      return nameMatches || dialCodeMatches;
    });
  }, [filter, locale, options]);

  useEffect(() => {
    setActiveIndex((index) => Math.min(index, Math.max(filteredOptions.length - 1, 0)));
  }, [filteredOptions.length]);

  const activeOption = filteredOptions[activeIndex];

  useEffect(() => {
    if (open && activeOption && typeof activeOptionRef.current?.scrollIntoView === "function") {
      activeOptionRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [activeOption, open]);

  const updateValue = (nextCountry: TCountryCode, nextNationalNumber: string) => {
    const nextOption = options.find((option) => option.code === nextCountry) ?? selectedOption;
    setCountry(nextCountry);
    setNationalNumber(nextNationalNumber);
    field.onChange(composePhoneValue(nextOption.code, nextOption.dialCode, nextNationalNumber));
    onChange?.();
  };

  const selectOption = (option: DialCodeOption) => {
    updateValue(option.code, nationalNumber);
    setFilter("");
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerClick = () => {
    if (open) {
      setOpen(false);
      return;
    }

    setFilter("");
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(Math.max(filteredOptions.length - 1, 0));
    } else if (event.key === "Enter" && filteredOptions[activeIndex]) {
      event.preventDefault();
      selectOption(filteredOptions[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} className={className}>
      <div className="gcw:relative gcw:flex gcw:gap-2">
        <button
          ref={triggerRef}
          type="button"
          className="container-inputs gcw:shrink-0 gcw:flex gcw:items-center gcw:gap-1 gcw:w-auto"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${t("phone_country_code", "Country code")}: ${selectedOption.name} ${selectedOption.dialCode}`}
          onClick={handleTriggerClick}
        >
          <CountryFlag code={selectedOption.code} />
          <span>{selectedOption.dialCode}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        <div className="gcw:w-full">
          <label className={`gcw:block gcw:mb-2 ${labelClassName}`} htmlFor={id}>
            {label}
            {required && <span className="gcw:text-text-primary"> * </span>}
          </label>
          <input
            {...field}
            id={id}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            className={`container-inputs ${error ? "gcw:border-error" : ""}`}
            placeholder={required ? `${placeholder} *` : placeholder}
            value={nationalNumber}
            onChange={(event) => updateValue(country, event.target.value.replace(/[^\d\s().-]/g, ""))}
            onBlur={field.onBlur}
            aria-describedby={
              [error ? `${id}-error` : undefined, ariaDescribedBy].filter(Boolean).join(" ") || undefined
            }
            aria-invalid={error ? "true" : undefined}
            aria-required={required}
          />
        </div>
        {open && (
          <div className="gcw:absolute gcw:left-0 gcw:top-full gcw:z-10 gcw:mt-1 gcw:w-full gcw:max-w-80 gcw:max-h-80 gcw:overflow-y-auto gcw:bg-white container-inputs">
            <input
              ref={filterRef}
              type="search"
              role="combobox"
              className="container-inputs gcw:w-full"
              placeholder={t("phone_country_search", "Search countries")}
              aria-label={t("phone_country_search", "Search countries")}
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleFilterKeyDown}
              aria-expanded={true}
              aria-autocomplete="list"
              aria-controls={`${id}-country-list`}
              aria-activedescendant={activeOption ? `${id}-option-${activeOption.code}` : undefined}
            />
            <ul
              id={`${id}-country-list`}
              role={LISTBOX_ROLE}
              tabIndex={-1}
              aria-label={t("phone_country_code", "Country code")}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  // ARIA listbox option; pointer selection is part of the listbox pattern,
                  // keyboard nav lives on the combobox input via aria-activedescendant.
                  <li // NOSONAR
                    id={`${id}-option-${option.code}`}
                    key={option.code}
                    ref={option.code === activeOption?.code ? activeOptionRef : undefined}
                    role={OPTION_ROLE}
                    tabIndex={-1}
                    aria-selected={option.code === country}
                    className={`gcw:w-full gcw:hover:bg-gray-50 ${
                      option.code === activeOption?.code ? "gcw:bg-medium-grey" : ""
                    } ${option.code === country ? "gcw:font-semibold" : ""}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => {
                      selectOption(option);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectOption(option);
                      }
                    }}
                  >
                    <div className="gcw:flex gcw:w-full gcw:items-center gcw:gap-2 gcw:p-2 gcw:text-left">
                      <CountryFlag code={option.code} />
                      <span>{option.name}</span>
                      <span className="gcw:ml-auto">{option.dialCode}</span>
                    </div>
                  </li>
                ))
              ) : (
                <li className="gcw:p-2">{t("phone_country_no_results", "No countries found")}</li>
              )}
            </ul>
          </div>
        )}
      </div>
      {error && (
        <div id={`${id}-error`} role="alert" className="gcw:text-error gcw:text-md gcw:mt-1">
          {error.message}
        </div>
      )}
    </div>
  );
}

export default PhoneInput;
